import Decimal from "decimal.js";
import { convert, formatAmount } from "./convert";
import { amountToDecimal } from "./parseAmount";
import type { RateSnapshot } from "../rates/fixture";

/**
 * Tapping a quote row makes it the base while preserving the displayed value.
 *
 * WHY THIS NEEDS TWO FIELDS RATHER THAN ONE
 * -----------------------------------------
 * The requirement is that nothing appears to change except which field is
 * editable. Base SAR 100 shows USD as 26.67; tap USD and the field must read
 * 26.67 while SAR goes back to exactly 100.
 *
 * Those two facts are incompatible with a single scalar. 100 SAR is 26.666…
 * USD, which DISPLAYS as 26.67 — but 26.67 USD is 100.0125 SAR, which displays
 * as 100.01. Carrying only the rounded string makes the old base visibly drift
 * every time the user swaps, and the drift compounds on repeated swaps. That is
 * exactly the bug most converters ship.
 *
 * So the amount carries both:
 *   input  — the string the field shows, and the raw string the user typed
 *   exact  — the full-precision value, present ONLY when the amount was derived
 *            by a swap rather than typed
 *
 * `exact` wins for maths while it exists, so the reverse conversion is lossless.
 * The moment the user edits the field, `exact` is discarded and the typed string
 * becomes authoritative again — a typed "26.67" really does mean 26.67.
 */
export interface AmountState {
  readonly input: string;
  readonly exact: Decimal | null;
}

export function typedAmount(input: string): AmountState {
  return { input, exact: null };
}

/** The value all conversions should use. */
export function amountValue(state: AmountState): Decimal {
  return state.exact ?? amountToDecimal(state.input);
}

export interface SwapResult {
  readonly base: string;
  readonly amount: AmountState;
}

export function swapBase(
  amount: AmountState,
  currentBase: string,
  nextBase: string,
  snapshot: RateSnapshot,
): SwapResult {
  if (currentBase === nextBase) return { base: currentBase, amount };

  const exact = convert(amountValue(amount), currentBase, nextBase, snapshot);

  return {
    base: nextBase,
    amount: {
      // The field shows the same number the row was already showing.
      input: formatAmount(exact, nextBase, false),
      // …but the maths keeps every digit, so swapping back is lossless.
      exact,
    },
  };
}

/** Zero is the resting state for an empty field, never blank and never stale. */
export const ZERO_AMOUNT: AmountState = { input: "", exact: null };

export function isZeroAmount(state: AmountState): boolean {
  return amountValue(state).isZero();
}

export function decimalFrom(value: Decimal.Value): Decimal {
  return new Decimal(value);
}
